import { useCallback, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { Box, Column, FloatingContainer, Icon, Row, Screen, Text, ZStack } from '~/commonComponents';
import { BackButtonProxy as BackButton } from '~/commonComponents/BackButton';
import Button from '~/commonComponents/Button';
import { SectionCard } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { Colors } from '~/themes/colors';
import {
  type IShiftDetailsFormValues,
  type IShiftOverride,
} from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from '../ShiftDetails/SpeedUpCoverage/AutoConfirmField';
import { EditShiftForm } from './EditShiftForm';
import { ReadOnlyDatePill } from './ReadOnlyDatePill';

export const EditShiftScreen = () => {
  const { dateIndex } = useParams<{ dateIndex: string }>();
  const navigate = useNavigate();
  const { getValues, setValue: setSharedValue } = useFormContext<IShiftDetailsFormValues>();

  const [openField, setOpenField] = useState<string | null>(null);
  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  const sharedValues = getValues();
  const index = Number(dateIndex);
  const date = sharedValues.dates[index];

  const dateKey = date?.toISOString() ?? '';
  const existingOverride = sharedValues.shiftOverrides?.[dateKey];

  // Compute initial defaults only once on mount to prevent form reset on re-renders
  const localDefaults = useMemo(
    () => ({
      ...sharedValues,
      ...(existingOverride ?? {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Snapshot shared values once on mount to prevent form reset
    [],
  );

  const {
    control,
    watch,
    getValues: getLocalValues,
  } = useForm<IShiftDetailsFormValues>({
    defaultValues: localDefaults,
  });

  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');

  const officeName = useAppSelector((state) => state.user.selectedChildOffice?.office_name) || '';

  const toggleField = useCallback((fieldName: string) => {
    setOpenField((current) => (current === fieldName ? null : fieldName));
  }, []);

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [allowNavigation, navigate]);

  const handleSave = useCallback(() => {
    const localValues = getLocalValues();
    const override: IShiftOverride = {
      preferredSpecialty: localValues.preferredSpecialty,
      isAssistedHygiene: localValues.isAssistedHygiene,
      arrivalTime: localValues.arrivalTime,
      departureTime: localValues.departureTime,
      mealBreakDuration: localValues.mealBreakDuration,
      isMealBreakPaid: localValues.isMealBreakPaid,
      rate: localValues.rate,
      procedures: localValues.procedures,
      autoConfirm: localValues.autoConfirm,
    };

    const currentOverrides = getValues('shiftOverrides') || {};
    setSharedValue('shiftOverrides', {
      ...currentOverrides,
      [dateKey]: override,
    });

    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [getLocalValues, getValues, setSharedValue, dateKey, allowNavigation, navigate]);

  if (!date) {
    navigate('/dashboard/multi-day/confirm', { replace: true });
    return null;
  }

  return (
    <Screen minWidth={768} paddingInline={30} paddingTop={16} paddingBottom={16}>
      <FloatingContainer justifyContent="flex-start" maxWidth={1120} borderRadius={0} margin={0} minWidth={0} width="100%" alignSelf="center">
        <Row alignItems="center" marginTop={60} position="relative">
          <ZStack left={20}>
            <BackButton onClick={handleBack} />
          </ZStack>
          <Text bold fontSize={28} textAlign="center" flex={1}>
            Edit Shift
          </Text>
        </Row>

        <Column paddingHorizontal={28} paddingTop={8} paddingBottom={60}>
          <Row marginTop={32} gap={24} flexWrap="wrap">
            <Column flex={1} minWidth={430}>
              <SectionCard title="Shift Details" testId="edit-shift-details-card">
                <EditShiftForm
                  control={control}
                  watch={watch}
                  officeName={officeName}
                  openField={openField}
                  toggleField={toggleField}
                />
              </SectionCard>
            </Column>

            <Column flex={1} minWidth={430}>
              <div
                data-testid="edit-shift-date-card"
                style={{
                  backgroundColor: Colors.white,
                  borderRadius: 8,
                  height: 80,
                  padding: '0px 28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: `1.5px solid ${Colors.neutral_100}`,
                }}
              >
                <Row alignItems="center" gap={6}>
                  <Text bold fontSize={24} color="neutral_600">
                    Date
                  </Text>
                  <Text color="neutral_500" marginTop={4}>
                    (Read-Only)
                  </Text>
                </Row>
                <ReadOnlyDatePill date={date} />
              </div>

              <Box marginTop={24}>
                <SectionCard title="Speed Up Your Coverage" testId="edit-speed-up-card">
                  <AutoConfirmField
                    control={control}
                    professionType={professionType}
                    preferredSpecialty={preferredSpecialty}
                  />
                </SectionCard>
              </Box>
            </Column>
          </Row>

          <Button
            text="Save Changes"
            data-testid="save-changes-button"
            onClick={handleSave}
            style={{
              width: '100%',
              maxWidth: 296,
              margin: '40px auto 0',
              display: 'block',
              backgroundColor: Colors.primary_500,
            }}
          />
        </Column>
      </FloatingContainer>

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </Screen>
  );
};
